import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Button, Form, Modal } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { RoomTypeCategory } from "../models/roomTypeCategory.model";
import { categoryService } from "../service/category.service";
import { roomTypeService } from "../service/roomType.service";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import PageHeader from "./PageHeader";

const initialForm: Partial<RoomTypeCategory> = {
  id: 0,
  name: "",
  description: "",
  roomTypeId: 0,
  isActive: true,
};

const RoomTypeCategories: React.FC = () => {
  const [categories, setCategories] = useState<RoomTypeCategory[]>([]);
  const [roomTypeOptions, setRoomTypeOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [form, setForm] = useState<Partial<RoomTypeCategory>>(initialForm);
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState<number | "">("");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const fetchRoomTypes = async () => {
    try {
      const res = await roomTypeService.getAll();
      const mapped = res.map((r: any) => ({
        label: r.name,
        value: r.id,
      }));
      setRoomTypeOptions(mapped);
    } catch {
      setRoomTypeOptions([]);
    }
  };

  const fetchCategoriesByRoomTypeId = async (id: number) => {
    try {
      const res = await categoryService.getByRoomTypeId(id);
      setCategories(res);
      console.log("Fetched categories:", res);
    } catch {
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  useEffect(() => {
    if (selectedRoomTypeId) {
      fetchCategoriesByRoomTypeId(selectedRoomTypeId);
    } else {
      setCategories([]);
    }
  }, [selectedRoomTypeId]);

  const validate = () => {
    const errs: { [key: string]: string } = {};
    if (!form.name?.trim()) errs.name = "Name is required";
    if (!form.roomTypeId) errs.roomTypeId = "Room Type is required";
    return errs;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "roomTypeId" ? Number(value) : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      if (form.id) {
        const { id, name, description, roomTypeId, isActive } = form;

        const payload: RoomTypeCategory = {
          id: id || 0,
          name: name?.trim() || "",
          description: description?.trim() || "",
          roomTypeId: roomTypeId!,
          isActive: isActive ?? true,
        };
        await categoryService.update(form.id, payload);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Category updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        await categoryService.create(form as RoomTypeCategory);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Category created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
      setForm(initialForm);
      setShowModal(false);
      if (selectedRoomTypeId) fetchCategoriesByRoomTypeId(selectedRoomTypeId);
    } catch {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to save category.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (data: RoomTypeCategory) => {
    setForm({
      ...data,
      roomTypeId: data.roomTypeId,
    });
    setShowModal(true);
  };

  const toggleStatus = async (data: RoomTypeCategory) => {
    try {
      await categoryService.updateStatus(data.id);
      if (selectedRoomTypeId) fetchCategoriesByRoomTypeId(selectedRoomTypeId);
    } catch {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to update status.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  };

  const actionTemplate = (row: RoomTypeCategory) => (
    <div className="d-flex align-items-center gap-2">
      <Button
        variant="link"
        className="p-0 text-success"
        onClick={() => handleEdit(row)}
        title="Edit"
      >
        <Icon.PencilSquare size={20} />
      </Button>
      <Button
        variant="link"
        className="p-0"
        onClick={() => toggleStatus(row)}
        style={{ color: row.isActive ? "#198754" : "#dc3545" }}
      >
        {row.isActive ? <Icon.Unlock size={20} /> : <Icon.Lock size={20} />}
      </Button>
    </div>
  );

  return (
    <div className="content">
              <PageHeader
          title="Room Categories"
          showBackButton={false}
        />
 
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow p-3">
            <div className="col-md-2 mb-2">
              <div className="d-flex justify-content-between align-items-center gap-3">
                <IconField iconPosition="left" className="flex-grow-1">
                  <InputIcon>
                    <Icon.Search className="tx-12" />
                  </InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder="Global Search"
                  />
                </IconField>
              </div>
            </div>
            <div className="col-md-2 mb-3">
              <IconField iconPosition="left" className="flex-grow-1">
                <InputIcon>
                  <span
                    className="position-absolute"
                    style={{ left: "28px", top: "35%" }}
                  >
                    <Icon.List className="tx-12" />
                  </span>{" "}
                </InputIcon>
                <div className="p-inputwrapper w-100 ms-4">
                  <Form.Select
                    className="form-select"
                    value={selectedRoomTypeId}
                    onChange={(e) =>
                      setSelectedRoomTypeId(Number(e.target.value))
                    }
                    style={{
                      height: "48px",
                      paddingLeft: "2rem",
                    }}
                  >
                    <option value="">Select Room Type</option>
                    {roomTypeOptions.map((rt) => (
                      <option key={rt.value} value={rt.value}>
                        {rt.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </IconField>
            </div>

            <div className="col-md-8 text-end mb-3">
              <Button
                onClick={() => {
                  setShowModal(true);
                  setForm(initialForm);
                  setErrors({});
                }}
                className="btn btn-primary"
              >
                <Icon.PlusCircle className="me-1" /> Create Category
              </Button>
            </div>
            <div className="col-md-12">
              <DataTable
                value={categories.filter((cat) =>
                  [cat.name, cat.description].some((f) =>
                    f?.toLowerCase().includes(globalFilter.toLowerCase())
                  )
                )}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 20]}
                className="grid-table"
                dataKey="id"
                emptyMessage={
                  selectedRoomTypeId === ""
                    ? "Select Room Type"
                    : "No Room Categories Found."
                }
                globalFilter={globalFilter}
              >
                <Column field="name" header="Name" sortable />
                <Column field="description" header="Description" sortable />
                <Column
                  field="roomType.name" 
                  header="Room Type" sortable
                  body={(row) => row.roomType?.name || "—"}
                />
                <Column
                  field="isActive"
                  header="Status" sortable
                  body={(row) => (row.isActive ? "Active" : "InActive")}
                />
                <Column
                  header="Actions"
                  body={actionTemplate}
                  style={{ minWidth: "120px" }}
                />
              </DataTable>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        size="lg"
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {form.id ? "Update" : "Create"} Room Type Category
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-2">
            <div className="col-6">
              <Form.Label>
                Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="name"
                value={form.name || ""}
                onChange={handleChange}
                isInvalid={!!errors.name}
              />
              {errors.name && (
                <div className="text-danger tx-13">{errors.name}</div>
              )}
            </div>

            <div className="col-6">
              <Form.Label>
                Room Type <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="roomTypeId"
                value={form.roomTypeId || ""}
                onChange={handleChange}
                isInvalid={!!errors.roomTypeId}
              >
                <option value="">Select Room Type</option>
                {roomTypeOptions.map((rt) => (
                  <option key={rt.value} value={rt.value}>
                    {rt.label}
                  </option>
                ))}
              </Form.Select>
              {errors.roomTypeId && (
                <div className="text-danger tx-13">{errors.roomTypeId}</div>
              )}
            </div>

            <div className="col-12">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={form.description || ""}
                onChange={handleChange}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
          <Button
            className="btn btn-secondary"
            onClick={() => setShowModal(false)}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RoomTypeCategories;
